import Dexie, { Table } from 'dexie'

import type { CreateHighlightRequest } from '@/api/services/highlight'
import { EpisodeList, GradeRecordingResponse, TranscribeResponse } from '@/api/type'

export type StoredAudio = {
  id: string // `${domain}-${productId}-${episodeId}`
  domain: string
  productId: string
  episodeId: string
  title: string
  audioBlob: Blob
  transcription: TranscribeResponse | null
  savedAt: number
  fileSize: number
}

export type StoredEpisodeList = {
  id: string // 'english-episodes' (domain固定)
  domain: string
  episodes: EpisodeList[]
  savedAt: number
}

export type StoredRecording = {
  id: string // `${domain}-${productId}-${episodeId}-${timestamp}`
  domain: string
  productId: string
  episodeId: string
  audioBlob: Blob
  duration: number // 録音時間（秒）
  savedAt: number
  fileSize: number
  gradingResult?: GradeRecordingResponse & {
    gradedAt: number
  }
}

export type StoredExternalVideo = {
  id: string // video_id
  title: string
  videoBlob: Blob
  thumbnailBlob: Blob | null
  originalUrl: string
  savedAt: number
  fileSize: number
  memo?: string
  order: number // 並び順
  quality?: 'high' | 'medium' | 'low' // 画質
}

export type StoredExternalMusic = {
  id: string // music_id
  title: string
  artist: string | null
  audioBlob: Blob
  thumbnailBlob: Blob | null
  originalUrl: string
  savedAt: number
  fileSize: number
  duration: number | null // 秒
  memo?: string
  order: number // 並び順
}

export type MusicPlaylist = {
  id: string // crypto.randomUUID()
  name: string
  musicIds: string[] // 曲IDの配列（順序も兼ねる）
  createdAt: number
  updatedAt: number
  order: number // プレイリスト一覧の並び順
}

// ============================================================
// オフライン再生用の型定義
// ============================================================

/** 音楽エピソードの付随情報（曲一覧をオフラインだけで組み立てるために保存する） */
export type OfflineMusicMeta = {
  artist: string | null
  /** アーティストのタグID（アーティスト画面への遷移・絞り込みに使う） */
  artistTagId: number | null
  albumTitle: string
  /** 再生時間（秒） */
  duration: number
}

/** ダウンロード済みエピソードBlob */
export type OfflineEpisode = {
  episodeId: number // PK
  productId: number
  domain: string
  title: string
  mimeType: string
  blob: Blob
  fileSize: number
  downloadedAt: number
  thumbnailBlob?: Blob
  /** 保存した画質（'original' | '720p' | '480p'。未設定は原本） */
  quality?: string
  /** 音楽ドメインのみ設定される付随情報 */
  music?: OfflineMusicMeta
}

/** IndexedDB は boolean をインデックスにできないため、0/1 で持つ */
export type DbFlag = 0 | 1

/** ローカルファーストのエピソード進捗 */
export type OfflineProgress = {
  episodeId: number // PK
  progress: number
  cfi: string | null
  /** Readium Locator JSON（EPUB） */
  locator?: string | null
  isCompleted: boolean
  updatedAt: number // ms epoch
  synced: DbFlag // サーバーと同期済みか
}

/** ローカルファーストのハイライト */
export type OfflineHighlight = {
  localId: string // PK — サーバから取得した分は `s<id>`、端末で作った分は `t<n>`（画面上の id は -n）
  serverId: number | null // サーバー割当ID（同期後に設定）
  episodeId: number
  data: CreateHighlightRequest
  note: string | null
  color: string
  createdAt: number
  updatedAt: number
  deleted: DbFlag // 論理削除（サーバから消えたら物理削除）
  synced: DbFlag
}

/** 閲覧キャッシュ（LRU-1: 最新1エピソードのみ保持） */
export type ViewCache = {
  episodeId: number // PK
  mimeType: string
  blob: Blob
  fileSize: number
  cachedAt: number
}

/** 閲覧キャッシュ（画像ページ単位） */
export type ViewCachePage = {
  cacheKey: string // PK — `${episodeId}-${pageIndex}`
  episodeId: number
  pageIndex: number
  blob: Blob
  cachedAt: number
}

/** 同期キュー */
export type SyncAction = {
  id?: number // auto-increment
  action: 'update_progress' | 'create_highlight' | 'update_highlight' | 'delete_highlight'
  payload: Record<string, unknown>
  createdAt: number
  retryCount: number
}

/** 画像キャッシュ（LRU + TTL） */
export type StoredImageCache = {
  key: string // PK — cacheKey (e.g. "shortcut-123", path)
  blob: Blob
  cachedAt: number
  lastAccessedAt: number
}

/**
 * ダウンロード中間チャンク（レジューム用）
 *
 * 大容量ファイルを丸ごとメモリに載せるとタブがクラッシュするため、
 * 一定サイズごとに Blob へ切り出して逐次保存する。
 * ダウンロード完了時に結合して offlineEpisodes へ移し、本テーブルからは削除する。
 */
export type DownloadChunk = {
  episodeId: number
  index: number // 0 始まりの連番
  blob: Blob
  size: number
}

/**
 * 中断中ダウンロードのメタ情報
 *
 * downloadChunks だけではタイトル等が分からず UI に提示できないため、
 * 進行状態をこのテーブルで併せて管理する。ダウンロード完了時に削除される。
 */
export type PendingDownload = {
  episodeId: number // PK
  productId: number
  domain: string
  title: string
  mimeType: string
  /** 保存済みバイト数 */
  receivedBytes: number
  /** 全体サイズ（不明な場合は 0） */
  totalBytes: number
  /** ダウンロード中の画質（再開時に同じ画質を使うために保持） */
  quality?: string
  updatedAt: number
}

export class AudioDatabase extends Dexie {
  audios!: Table<StoredAudio>
  episodeLists!: Table<StoredEpisodeList>
  recordings!: Table<StoredRecording>
  externalVideos!: Table<StoredExternalVideo>
  externalMusic!: Table<StoredExternalMusic>
  musicPlaylists!: Table<MusicPlaylist>
  offlineEpisodes!: Table<OfflineEpisode>
  offlineProgress!: Table<OfflineProgress>
  offlineHighlights!: Table<OfflineHighlight>
  syncQueue!: Table<SyncAction>
  viewCache!: Table<ViewCache>
  viewCachePages!: Table<ViewCachePage>
  imageCache!: Table<StoredImageCache>
  downloadChunks!: Table<DownloadChunk>
  pendingDownloads!: Table<PendingDownload>

  constructor() {
    super('AudioDatabase')
    this.version(3).stores({
      audios: 'id, domain, productId, episodeId, savedAt',
      episodeLists: 'id, domain, savedAt',
      recordings: 'id, [domain+productId+episodeId], savedAt',
    })
    this.version(4).stores({
      audios: 'id, domain, productId, episodeId, savedAt',
      episodeLists: 'id, domain, savedAt',
      recordings: 'id, [domain+productId+episodeId], savedAt',
    })
    this.version(5).stores({
      audios: 'id, domain, productId, episodeId, savedAt',
      episodeLists: 'id, domain, savedAt',
      recordings: 'id, [domain+productId+episodeId], savedAt',
      externalVideos: 'id, savedAt',
    })
    this.version(6).stores({
      audios: 'id, domain, productId, episodeId, savedAt',
      episodeLists: 'id, domain, savedAt',
      recordings: 'id, [domain+productId+episodeId], savedAt',
      externalVideos: 'id, savedAt',
      externalMusic: 'id, savedAt',
    })
    this.version(7).stores({
      audios: 'id, domain, productId, episodeId, savedAt',
      episodeLists: 'id, domain, savedAt',
      recordings: 'id, [domain+productId+episodeId], savedAt',
      externalVideos: 'id, savedAt',
      externalMusic: 'id, savedAt',
      musicPlaylists: 'id, order',
    })
    this.version(8).stores({
      audios: 'id, domain, productId, episodeId, savedAt',
      episodeLists: 'id, domain, savedAt',
      recordings: 'id, [domain+productId+episodeId], savedAt',
      externalVideos: 'id, savedAt',
      externalMusic: 'id, savedAt',
      musicPlaylists: 'id, order',
      offlineEpisodes: 'episodeId, productId, domain, downloadedAt',
      offlineProgress: 'episodeId, synced',
      offlineHighlights: 'localId, episodeId, serverId, synced, deleted',
      syncQueue: '++id, createdAt',
    })
    this.version(9).stores({
      audios: 'id, domain, productId, episodeId, savedAt',
      episodeLists: 'id, domain, savedAt',
      recordings: 'id, [domain+productId+episodeId], savedAt',
      externalVideos: 'id, savedAt',
      externalMusic: 'id, savedAt',
      musicPlaylists: 'id, order',
      offlineEpisodes: 'episodeId, productId, domain, downloadedAt',
      offlineProgress: 'episodeId, synced',
      offlineHighlights: 'localId, episodeId, serverId, synced, deleted',
      syncQueue: '++id, createdAt',
      viewCache: 'episodeId',
      viewCachePages: 'cacheKey, episodeId',
    })
    this.version(10).stores({
      audios: 'id, domain, productId, episodeId, savedAt',
      episodeLists: 'id, domain, savedAt',
      recordings: 'id, [domain+productId+episodeId], savedAt',
      externalVideos: 'id, savedAt',
      externalMusic: 'id, savedAt',
      musicPlaylists: 'id, order',
      offlineEpisodes: 'episodeId, productId, domain, downloadedAt',
      offlineProgress: 'episodeId, synced',
      offlineHighlights: 'localId, episodeId, serverId, synced, deleted',
      syncQueue: '++id, createdAt',
      viewCache: 'episodeId',
      viewCachePages: 'cacheKey, episodeId',
      imageCache: 'key, lastAccessedAt',
    })
    this.version(11).stores({
      audios: 'id, domain, productId, episodeId, savedAt',
      episodeLists: 'id, domain, savedAt',
      recordings: 'id, [domain+productId+episodeId], savedAt',
      externalVideos: 'id, savedAt',
      externalMusic: 'id, savedAt',
      musicPlaylists: 'id, order',
      offlineEpisodes: 'episodeId, productId, domain, downloadedAt',
      offlineProgress: 'episodeId, synced',
      offlineHighlights: 'localId, episodeId, serverId, synced, deleted',
      syncQueue: '++id, createdAt',
      viewCache: 'episodeId',
      viewCachePages: 'cacheKey, episodeId',
      imageCache: 'key, lastAccessedAt',
      downloadChunks: '[episodeId+index], episodeId',
      pendingDownloads: 'episodeId, updatedAt',
    })
    // synced / deleted を boolean から 0/1 に変えてインデックスで引けるようにする
    this.version(12)
      .stores({
        offlineProgress: 'episodeId, synced',
        offlineHighlights: 'localId, episodeId, serverId, synced, deleted',
      })
      .upgrade(async (tx) => {
        await tx
          .table<OfflineProgress>('offlineProgress')
          .toCollection()
          .modify((p) => {
            p.synced = p.synced ? 1 : 0
          })
        await tx
          .table<OfflineHighlight>('offlineHighlights')
          .toCollection()
          .modify((h) => {
            h.synced = h.synced ? 1 : 0
            h.deleted = h.deleted ? 1 : 0
          })
      })
  }
}

export const db = new AudioDatabase()

// 音声データを保存
export const saveAudio = async (
  domain: string,
  productId: string,
  episodeId: string,
  title: string,
  audioBlob: Blob,
  transcription: TranscribeResponse | null = null,
) => {
  const id = `${domain}-${productId}-${episodeId}`
  await db.audios.put({
    id,
    domain,
    productId,
    episodeId,
    title,
    audioBlob,
    transcription,
    savedAt: Date.now(),
    fileSize: audioBlob.size,
  })
}

// Transcribeデータのみを更新
export const updateTranscription = async (
  domain: string,
  productId: string,
  episodeId: string,
  transcription: TranscribeResponse,
) => {
  const id = `${domain}-${productId}-${episodeId}`
  const existing = await db.audios.get(id)
  if (existing) {
    await db.audios.update(id, { transcription })
  }
}

// 音声データを取得
export const getAudio = async (domain: string, productId: string, episodeId: string) => {
  const id = `${domain}-${productId}-${episodeId}`
  return await db.audios.get(id)
}

// 音声データを削除
export const deleteAudio = async (domain: string, productId: string, episodeId: string) => {
  const id = `${domain}-${productId}-${episodeId}`
  await db.audios.delete(id)
}

// すべての音声データを取得
export const getAllAudios = async () => {
  return await db.audios.orderBy('savedAt').reverse().toArray()
}

// 合計容量を計算
export const getTotalSize = async () => {
  const audios = await db.audios.toArray()
  return audios.reduce((sum, audio) => sum + audio.fileSize, 0)
}

// エピソードリストを保存（englishドメインのみ、最新のものを1つだけ）
export const saveEpisodeList = async (domain: string, episodes: EpisodeList[]) => {
  if (domain !== 'english') return

  const id = `${domain}-episodes`
  await db.episodeLists.put({
    id,
    domain,
    episodes,
    savedAt: Date.now(),
  })
}

// エピソードリストを取得
export const getEpisodeList = async (domain: string) => {
  if (domain !== 'english') return null

  const id = `${domain}-episodes`
  return await db.episodeLists.get(id)
}

// エピソードリストを削除
export const deleteEpisodeList = async (domain: string) => {
  const id = `${domain}-episodes`
  await db.episodeLists.delete(id)
}

// 録音データを保存
export const saveRecording = async (
  domain: string,
  productId: string,
  episodeId: string,
  audioBlob: Blob,
  duration: number,
) => {
  const timestamp = Date.now()
  const id = `${domain}-${productId}-${episodeId}-${timestamp}`
  await db.recordings.put({
    id,
    domain,
    productId,
    episodeId,
    audioBlob,
    duration,
    savedAt: timestamp,
    fileSize: audioBlob.size,
  })
  return id
}

// 特定のエピソードの録音データを取得
export const getRecordings = async (domain: string, productId: string, episodeId: string) => {
  return await db.recordings
    .where('[domain+productId+episodeId]')
    .equals([domain, productId, episodeId])
    .reverse()
    .toArray()
}

// 録音データを削除
export const deleteRecording = async (id: string) => {
  await db.recordings.delete(id)
}

// すべての録音データを取得
export const getAllRecordings = async () => {
  return await db.recordings.orderBy('savedAt').reverse().toArray()
}

// 録音の採点結果を保存
export const saveGradingResult = async (id: string, result: GradeRecordingResponse) => {
  const recording = await db.recordings.get(id)
  if (!recording) {
    throw new Error('Recording not found')
  }

  await db.recordings.update(id, {
    gradingResult: {
      ...result,
      gradedAt: Date.now(),
    },
  })
}

// ============================================================
// オフラインエピソード
// ============================================================

export const saveOfflineEpisode = async (episode: OfflineEpisode) => {
  await db.offlineEpisodes.put(episode)
}

export const getOfflineEpisode = async (episodeId: number): Promise<OfflineEpisode | undefined> => {
  return db.offlineEpisodes.get(episodeId)
}

export const deleteOfflineEpisode = async (episodeId: number) => {
  await db.offlineEpisodes.delete(episodeId)
  // 関連するプログレスとハイライトも削除（サーバへ未送信のものは残す）
  await db.offlineProgress
    .where('episodeId')
    .equals(episodeId)
    .filter((p) => p.synced === 1)
    .delete()
  await db.offlineHighlights
    .where('episodeId')
    .equals(episodeId)
    .filter((h) => h.synced === 1)
    .delete()
  // 中断したダウンロードの中間チャンクも削除
  await db.downloadChunks.where('episodeId').equals(episodeId).delete()
  await db.pendingDownloads.delete(episodeId)
}

export const deleteOfflineEpisodesByProduct = async (productId: number) => {
  const episodes = await db.offlineEpisodes.where('productId').equals(productId).toArray()
  const episodeIds = episodes.map((e) => e.episodeId)
  await db.transaction(
    'rw',
    [db.offlineEpisodes, db.offlineProgress, db.offlineHighlights, db.downloadChunks, db.pendingDownloads],
    async () => {
      await db.offlineEpisodes.bulkDelete(episodeIds)
      await db.pendingDownloads.bulkDelete(episodeIds)
      for (const eid of episodeIds) {
        // サーバへ未送信の進捗・ハイライトは残す
        await db.offlineProgress
          .where('episodeId')
          .equals(eid)
          .filter((p) => p.synced === 1)
          .delete()
        await db.offlineHighlights
          .where('episodeId')
          .equals(eid)
          .filter((h) => h.synced === 1)
          .delete()
        await db.downloadChunks.where('episodeId').equals(eid).delete()
      }
    },
  )
}

export const getAllOfflineEpisodes = async (): Promise<OfflineEpisode[]> => {
  return db.offlineEpisodes.orderBy('downloadedAt').reverse().toArray()
}

export const getOfflineEpisodesByProduct = async (productId: number): Promise<OfflineEpisode[]> => {
  return db.offlineEpisodes.where('productId').equals(productId).toArray()
}

export const isEpisodeOffline = async (episodeId: number): Promise<boolean> => {
  const count = await db.offlineEpisodes.where('episodeId').equals(episodeId).count()
  return count > 0
}

/**
 * ダウンロード済みエピソードのファイルサイズを取得（未保存なら null）
 *
 * IndexedDB の Blob は参照として返るためレコード取得だけでは本体バイト列はメモリに載らない。
 * 保存済み判定も兼ねるので、サイズを使う場面では isEpisodeOffline の代わりに使える。
 */
export const getOfflineEpisodeSize = async (episodeId: number): Promise<number | null> => {
  const episode = await db.offlineEpisodes.get(episodeId)
  return episode?.fileSize ?? null
}

/**
 * プロダクト内の指定エピソードが全てオフライン保存済みかチェック
 */
export const isProductFullyOffline = async (episodeIds: number[]): Promise<boolean> => {
  if (episodeIds.length === 0) return false
  const count = await db.offlineEpisodes.where('episodeId').anyOf(episodeIds).count()
  return count === episodeIds.length
}

export const getOfflineEpisodesTotalSize = async (): Promise<number> => {
  const episodes = await db.offlineEpisodes.toArray()
  return episodes.reduce((sum, ep) => sum + ep.fileSize, 0)
}

// ============================================================
// ダウンロード中間チャンク（レジューム用）
// ============================================================

/** 保存済みチャンクを index 昇順で取得 */
export const getDownloadChunks = async (episodeId: number): Promise<DownloadChunk[]> => {
  const chunks = await db.downloadChunks.where('episodeId').equals(episodeId).toArray()
  return chunks.sort((a, b) => a.index - b.index)
}

/**
 * 中断地点（＝保存済みバイト数と次のチャンクindex）を取得する
 *
 * index に欠番がある場合は、連続している範囲までを有効とみなす。
 */
export const getDownloadResumeInfo = async (episodeId: number): Promise<{ bytes: number; nextIndex: number }> => {
  const chunks = await getDownloadChunks(episodeId)

  let bytes = 0
  let nextIndex = 0
  for (const chunk of chunks) {
    if (chunk.index !== nextIndex) break
    bytes += chunk.size
    nextIndex += 1
  }
  return { bytes, nextIndex }
}

/** チャンクを追記保存 */
export const putDownloadChunk = async (chunk: DownloadChunk) => {
  await db.downloadChunks.put(chunk)
}

/** 中間チャンクを全削除 */
export const clearDownloadChunks = async (episodeId: number) => {
  await db.downloadChunks.where('episodeId').equals(episodeId).delete()
}

// ============================================================
// 中断中ダウンロード（メタ情報）
// ============================================================

/** 中断中ダウンロードのメタ情報を作成／更新する */
export const upsertPendingDownload = async (pending: Omit<PendingDownload, 'updatedAt'>) => {
  await db.pendingDownloads.put({ ...pending, updatedAt: Date.now() })
}

export const getPendingDownload = async (episodeId: number): Promise<PendingDownload | undefined> => {
  return db.pendingDownloads.get(episodeId)
}

/** 中断中ダウンロードを新しい順に取得 */
export const getAllPendingDownloads = async (): Promise<PendingDownload[]> => {
  return db.pendingDownloads.orderBy('updatedAt').reverse().toArray()
}

/** 中間チャンクとメタ情報をまとめて破棄する */
export const discardPendingDownload = async (episodeId: number) => {
  await db.transaction('rw', [db.downloadChunks, db.pendingDownloads], async () => {
    await db.downloadChunks.where('episodeId').equals(episodeId).delete()
    await db.pendingDownloads.delete(episodeId)
  })
}

/**
 * 中断中ダウンロードを一括破棄する
 *
 * @param exceptEpisodeId 破棄対象から除外するエピソードID（実行中のダウンロード）
 * @returns 解放されたバイト数
 */
export const discardAllPendingDownloads = async (exceptEpisodeId?: number): Promise<number> => {
  const pendings = await db.pendingDownloads.toArray()
  const targets = pendings.filter((p) => p.episodeId !== exceptEpisodeId)

  for (const target of targets) {
    await discardPendingDownload(target.episodeId)
  }
  return targets.reduce((sum, p) => sum + p.receivedBytes, 0)
}

/**
 * 一定期間更新されていない中断中ダウンロードを削除する
 *
 * @returns 削除した件数
 */
export const cleanupStalePendingDownloads = async (maxAgeMs: number): Promise<number> => {
  const threshold = Date.now() - maxAgeMs
  const stale = await db.pendingDownloads.where('updatedAt').below(threshold).toArray()

  for (const pending of stale) {
    await discardPendingDownload(pending.episodeId)
  }
  return stale.length
}

// ============================================================
// オフラインプログレス（ローカルファースト）
// ============================================================

export const saveOfflineProgress = async (progress: OfflineProgress) => {
  await db.offlineProgress.put(progress)
}

export const getOfflineProgress = async (episodeId: number): Promise<OfflineProgress | undefined> => {
  return db.offlineProgress.get(episodeId)
}

export const getUnsyncedProgress = async (): Promise<OfflineProgress[]> => {
  return db.offlineProgress.where('synced').equals(0).toArray()
}

/** updatedAt を渡した場合は、その時刻の記録のまま（後から上書きされていない）ときだけ同期済みにする */
export const markProgressSynced = async (episodeId: number, updatedAt?: number) => {
  await db.offlineProgress
    .where('episodeId')
    .equals(episodeId)
    .filter((p) => updatedAt === undefined || p.updatedAt === updatedAt)
    .modify({ synced: 1 })
}

/** サーバの進捗を保存する。未同期で新しいローカルの記録があれば上書きしない */
export const cacheSyncedProgress = async (progress: OfflineProgress) => {
  await db.transaction('rw', db.offlineProgress, async () => {
    const local = await db.offlineProgress.get(progress.episodeId)
    if (local && !local.synced && local.updatedAt > progress.updatedAt) return
    await db.offlineProgress.put({ ...progress, synced: 1 })
  })
}

// ============================================================
// オフラインハイライト（ローカルファースト）
// ============================================================

export const saveOfflineHighlight = async (highlight: OfflineHighlight) => {
  await db.offlineHighlights.put(highlight)
}

export const getOfflineHighlightsByEpisode = async (episodeId: number): Promise<OfflineHighlight[]> => {
  return db.offlineHighlights
    .where('episodeId')
    .equals(episodeId)
    .filter((h) => !h.deleted)
    .toArray()
}

export const getOfflineHighlight = async (localId: string): Promise<OfflineHighlight | undefined> => {
  return db.offlineHighlights.get(localId)
}

export const findOfflineHighlightByServerId = async (serverId: number): Promise<OfflineHighlight | undefined> => {
  return db.offlineHighlights.where('serverId').equals(serverId).first()
}

export const deleteOfflineHighlight = async (localId: string) => {
  await db.offlineHighlights.delete(localId)
}

/**
 * サーバから取得したエピソードのハイライトで端末の記録を置き換える
 *
 * 未同期の記録（端末での作成・変更・削除）は残し、同期済みでサーバに無いものは消す。
 * 既存の記録は localId を引き継ぐ（画面が仮 ID のままでも探せるように）。
 */
export const replaceCachedHighlights = async (episodeId: number, server: OfflineHighlight[]) => {
  await db.transaction('rw', db.offlineHighlights, async () => {
    const existing = await db.offlineHighlights.where('episodeId').equals(episodeId).toArray()
    const byServerId = new Map(existing.flatMap((h) => (h.serverId === null ? [] : [[h.serverId, h] as const])))
    const serverIds = new Set(server.map((h) => h.serverId))

    const stale = existing.filter((h) => h.synced === 1 && h.serverId !== null && !serverIds.has(h.serverId))
    await db.offlineHighlights.bulkDelete(stale.map((h) => h.localId))

    const fresh = server.flatMap((h) => {
      const local = h.serverId === null ? undefined : byServerId.get(h.serverId)
      if (local && local.synced === 0) return []
      return [{ ...h, localId: local?.localId ?? h.localId }]
    })
    await db.offlineHighlights.bulkPut(fresh)
  })
}

export const getUnsyncedHighlights = async (): Promise<OfflineHighlight[]> => {
  return db.offlineHighlights.where('synced').equals(0).toArray()
}

/** 送信中に端末で更新されていたら（updatedAt が変わっていたら）未同期のままにする */
export const markHighlightSynced = async (localId: string, updatedAt: number, serverId?: number) => {
  await db.transaction('rw', db.offlineHighlights, async () => {
    const h = await db.offlineHighlights.get(localId)
    if (!h) return
    await db.offlineHighlights.put({
      ...h,
      serverId: serverId ?? h.serverId,
      synced: h.updatedAt === updatedAt ? 1 : 0,
    })
  })
}

// ============================================================
// 同期キュー
// ============================================================

export const enqueueSyncAction = async (action: SyncAction['action'], payload: Record<string, unknown>) => {
  await db.syncQueue.add({
    action,
    payload,
    createdAt: Date.now(),
    retryCount: 0,
  })
}

export const getPendingSyncActions = async (): Promise<SyncAction[]> => {
  return db.syncQueue.orderBy('createdAt').toArray()
}

export const removeSyncAction = async (id: number) => {
  await db.syncQueue.delete(id)
}

export const incrementSyncRetry = async (id: number) => {
  const action = await db.syncQueue.get(id)
  if (action) {
    await db.syncQueue.update(id, { retryCount: action.retryCount + 1 })
  }
}

// ============================================================
// 閲覧キャッシュ（LRU-1: 最新1エピソードのみ保持）
// ============================================================

/**
 * 閲覧キャッシュを保存（LRU-1: 異なるepisodeIdの既存キャッシュを削除）
 */
export const saveViewCache = async (episodeId: number, blob: Blob, mimeType: string) => {
  await db.transaction('rw', [db.viewCache, db.viewCachePages], async () => {
    // 異なるepisodeIdのキャッシュを全削除
    await db.viewCache.where('episodeId').notEqual(episodeId).delete()
    await db.viewCachePages.where('episodeId').notEqual(episodeId).delete()

    await db.viewCache.put({
      episodeId,
      mimeType,
      blob,
      fileSize: blob.size,
      cachedAt: Date.now(),
    })
  })
}

export const getViewCache = async (episodeId: number): Promise<ViewCache | undefined> => {
  return db.viewCache.get(episodeId)
}

/**
 * 画像ページキャッシュを保存（LRU-1: 異なるepisodeIdの既存キャッシュを削除）
 */
export const saveViewCachePages = async (episodeId: number, pages: { index: number; blob: Blob }[]) => {
  await db.transaction('rw', [db.viewCache, db.viewCachePages], async () => {
    // 異なるepisodeIdのキャッシュを全削除
    await db.viewCache.where('episodeId').notEqual(episodeId).delete()
    await db.viewCachePages.where('episodeId').notEqual(episodeId).delete()

    const entries: ViewCachePage[] = pages.map((p) => ({
      cacheKey: `${episodeId}-${p.index}`,
      episodeId,
      pageIndex: p.index,
      blob: p.blob,
      cachedAt: Date.now(),
    }))
    await db.viewCachePages.bulkPut(entries)
  })
}

export const getViewCachePages = async (episodeId: number): Promise<ViewCachePage[]> => {
  return db.viewCachePages.where('episodeId').equals(episodeId).sortBy('pageIndex')
}

export const clearAllViewCache = async () => {
  await db.transaction('rw', [db.viewCache, db.viewCachePages], async () => {
    await db.viewCache.clear()
    await db.viewCachePages.clear()
  })
}
